import { createSelector } from '@reduxjs/toolkit';
import { ChevronRightIcon } from 'lucide-preact';
import { useMemo } from 'preact/hooks';
import { useDispatch } from 'react-redux';

import { setProductsPromptModalState } from '@/components/products/ProductsPromptModal/redux/actions';
import { useLatestActiveTime } from '@/hooks/useLatestActiveDateTime';
import { ParsedUserProduct } from '@/queries/products/types';
import { AppState, useSelector } from '@/redux/helper';

import PurchaseAgainButton from './PurchaseAgainButton';
import UserProductImage from './UserProductImage';
import { getProductDescription, getProductPrice, getProductTitle, toProductType } from './utils';

interface UserProductRowProps {
  userProduct: ParsedUserProduct;
  onSelect?: () => void;
}

const UserProductRow = ({ userProduct, onSelect }: UserProductRowProps) => {
  const habitatId = userProduct.associatedHabitatIds?.[0];
  const selectHabitat = useMemo(
    () =>
      createSelector(
        (state: AppState) => state.allHabitats,
        (allHabitats) => {
          const habitat = allHabitats.find((habitat) => habitat._id === habitatId);
          return habitat
            ? {
                profileImage: habitat.profileImage,
                title: habitat.title,
              }
            : undefined;
        },
      ),
    [habitatId],
  );
  const habitat = useSelector(selectHabitat);
  const [now] = useLatestActiveTime();

  const title = getProductTitle(userProduct.product?.productLookupKey || userProduct.product?.name, habitat?.title);
  const description = getProductDescription(userProduct, now);
  const price = getProductPrice(userProduct.product?.price);

  const dispatch = useDispatch();

  const actions =
    // can purchase or subscribe again
    userProduct.eligibility?.allowed && userProduct.product?.productLookupKey ? (
      <>
        <div className="flex items-center justify-end gap-4 mobile:hidden">
          <div>{price}</div>
          <PurchaseAgainButton userProduct={userProduct} />
        </div>
        <button
          type="button"
          className="preflight preflight-button flex items-center justify-center desktop:hidden"
          onClick={() => {
            const primaryProductType = toProductType(userProduct.product?.productLookupKey);
            if (!primaryProductType) return;
            dispatch(
              setProductsPromptModalState({
                primaryProductType,
                habitatId: userProduct.associatedHabitatIds?.[0],
                entryPoint: 'purchase-again',
              }),
            );
          }}
        >
          <span className="sr-only">View details</span>
          <ChevronRightIcon className="size-5" />
        </button>
      </>
    ) : // active recurring or sponsorship products can be selected to show details
    userProduct.productKind === 'recurring' || userProduct.productKind === 'sponsorship' ? (
      <button type="button" className="preflight preflight-button flex items-center justify-center gap-3" onClick={onSelect}>
        <div className="mobile:hidden">{price}</div>
        <ChevronRightIcon className="size-5" />
      </button>
    ) : (
      // active product that have no details
      <div className="mobile:hidden desktop:mr-5">{price}</div>
    );

  return (
    <div className="preflight flex items-center gap-3 py-4 desktop:py-2">
      <UserProductImage className="size-11 shrink-0 desktop:size-[60px]" habitat={habitat} />
      <div className="flex min-w-0 grow flex-col">
        <h6 className="preflight preflight-h6 line-clamp-1 text-lg font-bold text-neutral-600 desktop:text-2xl">{title}</h6>
        <p className="preflight preflight-p line-clamp-1 text-base text-stone-500 desktop:text-xl">{description}</p>
      </div>
      <div className="text-base text-stone-500 desktop:text-xl">{actions}</div>
    </div>
  );
};

export default UserProductRow;
